import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import moment from "moment";
import { toast } from "@/shared/store/alertStore";
import { END_DATE_UNITS, EXPIRATION_DATE_KEY } from "@/shared/lib/constants";
import { StatusCode } from "@/shared/config/statusDictionary";
import { setNotarialEndDate } from "@/entities/transaction";

// Реэкспорт для обратной совместимости — единый источник в constants.js.
export { EXPIRATION_DATE_KEY };

const DOC_LANG_KEYS = {
  content: "ru",
  content_kz: "kz",
  content_en: "en",
};

function findMentionValue(node, key) {
  if (!node || typeof node !== "object") return undefined;
  if (node.type === "mention" && node.attrs?.key === key) {
    return node.attrs.value ?? "";
  }
  if (Array.isArray(node.content)) {
    for (const child of node.content) {
      const value = findMentionValue(child, key);
      if (value !== undefined) return value;
    }
  }
  return undefined;
}

// Минимальная задержка, чтобы спиннер успел показаться на быстрых действиях.
const MIN_FEEDBACK_MS = 400;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function useEndDateField(form) {
  const { t } = useI18n();

  const amount = ref(null);
  const unit = ref("");
  const loading = ref(false);
  const adding = ref(false);

  const availableUnits = computed(() => {
    const allowed = form?.end_date_units;
    if (Array.isArray(allowed) && allowed.length) {
      return END_DATE_UNITS.filter((u) => allowed.includes(u.code));
    }
    return END_DATE_UNITS;
  });

  watch(
    availableUnits,
    (units) => {
      if (unit.value || !units.length) return;
      const preferred = units.find((u) => u.code === "year") || units[0];
      unit.value = preferred.code;
    },
    { immediate: true }
  );

  const savedEndDate = computed(() =>
    form?.end_date ? moment(form.end_date).format("DD.MM.YYYY") : ""
  );

  const documentValue = computed(() => {
    for (const docKey of Object.keys(DOC_LANG_KEYS)) {
      const value = findMentionValue(form?.[docKey], EXPIRATION_DATE_KEY);
      if (value !== undefined) return value;
    }
    return undefined;
  });

  const hasMention = computed(() => documentValue.value !== undefined);

  // Подставлено, если во всех языковых версиях значение совпадает с датой.
  const isAdded = computed(() => {
    const date = savedEndDate.value;
    if (!date) return false;
    let matched = false;
    for (const docKey of Object.keys(DOC_LANG_KEYS)) {
      const value = findMentionValue(form?.[docKey], EXPIRATION_DATE_KEY);
      if (value === undefined) continue;
      if (value !== date) return false;
      matched = true;
    }
    return matched;
  });

  const isProject = computed(() => form?.status === StatusCode.PROJECT);

  const canSet = computed(
    () =>
      isProject.value && !!form?.id && Number(amount.value) > 0 && !!unit.value
  );

  const canAdd = computed(
    () => isProject.value && !!savedEndDate.value && !isAdded.value
  );

  async function applyEndDate() {
    if (!canSet.value) return;
    loading.value = true;
    try {
      const [data] = await Promise.all([
        setNotarialEndDate(form.id, {
          unit: unit.value,
          amount: Number(amount.value),
        }),
        sleep(MIN_FEEDBACK_MS),
      ]);
      // Ответ может быть плоским ({ ok, end_date }) или обёрнутым ({ success, payload }).
      const newEnd = data?.end_date ?? data?.payload?.end_date;
      if ((data?.ok || data?.success) && newEnd) {
        form.end_date = newEnd;
      }
    } finally {
      loading.value = false;
    }
  }

  // Способ вставки задаёт хост через insertFn (прямой JSON или эмит insert:field).
  async function addToDocument(insertFn) {
    const date = savedEndDate.value;
    if (!isProject.value || !date) return;

    if (!hasMention.value) {
      toast.warning(t("В документе нет поля «Дата окончания» для подстановки"));
      return;
    }

    if (isAdded.value) {
      toast.info(t("Дата окончания уже добавлена в документ"));
      return;
    }

    const isUpdate = !!documentValue.value;

    adding.value = true;
    try {
      await sleep(MIN_FEEDBACK_MS);
      insertFn({
        value: date,
        localizedValues: { ru: date, kz: date, en: date },
        key: EXPIRATION_DATE_KEY,
        label: "Дата окончания",
        isCustom: true,
      });
      toast.success(
        isUpdate
          ? t("Дата окончания обновлена в документе")
          : t("Дата окончания добавлена в документ")
      );
    } finally {
      adding.value = false;
    }
  }

  return {
    amount,
    unit,
    loading,
    adding,
    availableUnits,
    savedEndDate,
    documentValue,
    hasMention,
    isAdded,
    isProject,
    canSet,
    canAdd,
    applyEndDate,
    addToDocument,
  };
}
